/**
 * SourcesList - Liste des sources de surveillance avec toggles
 * Extrait de VeillePage.js pour améliorer la maintenabilité
 */
import React from 'react';
import { RefreshCw, Trash2, AlertCircle, CheckCircle2, Clock, Settings2, Zap } from 'lucide-react';
import { TYPE_CONFIG } from './UIComponents';

/**
 * Carte individuelle d'une source
 */
const SourceCard = ({
  source,
  isSelected,
  isRefreshing,
  onSelect,
  onToggle,
  onEdit,
  onRefresh,
  onDelete
}) => {
  const cfg = TYPE_CONFIG[source.type] || TYPE_CONFIG.rss;
  const Icon = cfg.icon;
  const isInactive = source.is_active === false;
  
  // Classes Tailwind pour les couleurs dynamiques
  const iconColorClasses = {
    cyan: 'text-cyan-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
  };

  return (
    <div
      className={`p-3 rounded-lg border transition-all cursor-pointer ${
        isInactive 
          ? 'bg-red-500/5 border-red-500/30 opacity-70' 
          : isSelected 
            ? 'bg-cyan-500/10 border-cyan-500/50' 
            : 'bg-slate-900/50 border-slate-800 hover:border-slate-600'
      }`}
      onClick={() => onSelect(source.id)}
      data-testid={`source-item-${source.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {/* Toggle actif/inactif */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(source);
            }}
            className={`w-8 h-4 rounded-full transition-all flex items-center ${
              isInactive ? 'bg-red-500/30' : 'bg-emerald-500/30'
            }`}
            title={isInactive 
              ? 'Activer la source (soumis au toggle master)' 
              : 'Désactiver la source (priorité sur le master)'}
            data-testid={`toggle-source-${source.id}`}
          >
            <div className={`w-3 h-3 rounded-full transition-all ${
              isInactive ? 'bg-red-400 ml-0.5' : 'bg-emerald-400 ml-4'
            }`} />
          </button>
          
          <Icon className={`w-3 h-3 ${iconColorClasses[cfg.color]} flex-shrink-0 ${isInactive ? 'opacity-50' : ''}`} />
          
          {!isInactive && (
            source.status === 'ok' ? (
              <CheckCircle2 className="w-3 h-3 text-green-400 flex-shrink-0" />
            ) : source.status === 'error' ? (
              <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
            ) : (
              <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />
            )
          )}
          
          <span className={`text-sm font-mono truncate ${isInactive ? 'text-slate-500 line-through' : 'text-slate-300'}`}>
            {source.name}
          </span>
          
          {source.auto_process && !isInactive && (
            <Zap className="w-3 h-3 text-amber-400 flex-shrink-0" title="Auto-traitement" />
          )}
          
          {isInactive && (
            <span className="text-[10px] text-red-400 uppercase ml-1">OFF</span>
          )}
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(source); }}
            className="p-1 hover:bg-slate-700 rounded"
            data-testid={`edit-source-${source.id}`}
          >
            <Settings2 className="w-3 h-3 text-slate-400" />
          </button>
          
          {!isInactive && (
            <button
              onClick={(e) => { e.stopPropagation(); onRefresh(source.id); }}
              className="p-1 hover:bg-slate-700 rounded"
              data-testid={`refresh-source-${source.id}`}
            >
              <RefreshCw className={`w-3 h-3 text-slate-400 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(source); }}
            className="p-1 hover:bg-red-900/30 rounded"
            data-testid={`delete-source-${source.id}`}
          >
            <Trash2 className="w-3 h-3 text-red-400" />
          </button>
        </div>
      </div>
      
      {/* Keywords */}
      {source.keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {source.keywords.slice(0, 3).map((kw, i) => (
            <span key={`${source.id}-kw-${i}`} className="text-[10px] bg-cyan-500/10 text-cyan-400 px-1.5 py-0.5 rounded font-mono">
              {kw}
            </span>
          ))}
          {source.keywords.length > 3 && (
            <span className="text-[10px] text-slate-500 font-mono">+{source.keywords.length - 3}</span>
          )}
        </div>
      )}
      
      {/* Schedule info */}
      <div className="text-[10px] text-slate-600 mt-1 font-mono">
        {source.scheduled_time ? `${source.scheduled_time} UTC` : `${source.refresh_interval_minutes} min`}
      </div>
    </div>
  );
};

/**
 * Composant principal : Liste des sources
 */
const SourcesList = ({
  sources,
  selectedSource,
  serviceStatus,
  refreshingSource,
  loading,
  onSelectSource,
  onToggleSource,
  onEditSource,
  onRefreshSource,
  onDeleteSource
}) => {
  // Trier : désactivées en premier
  const sortedSources = [...sources].sort((a, b) => {
    if (a.is_active === false && b.is_active !== false) return -1;
    if (a.is_active !== false && b.is_active === false) return 1;
    return 0;
  });

  return (
    <div className="lg:col-span-1 space-y-3">
      <h2 className="text-cyan-400 font-mono text-sm uppercase tracking-wider mb-2">Sources</h2>
      
      {/* Avertissement si le service master est arrêté */}
      {!serviceStatus.running && (
        <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-2" data-testid="master-off-warning">
          <p className="text-[10px] text-amber-400 font-mono flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Service arrêté — les toggles individuels n&apos;auront aucun effet tant que le service master n&apos;est pas activé.
          </p>
        </div>
      )}
      
      {/* Bouton "Toutes les sources" */}
      <button
        onClick={() => onSelectSource(null)}
        className={`w-full text-left p-3 rounded-lg border transition-all ${
          !selectedSource 
            ? 'bg-cyan-500/10 border-cyan-500/50' 
            : 'bg-slate-900/50 border-slate-800 hover:border-slate-600'
        }`}
        data-testid="filter-all-sources"
      >
        <span className="text-sm font-mono text-slate-300">Toutes les sources</span>
      </button>

      {/* Liste des sources */}
      {sortedSources.map(src => (
        <SourceCard
          key={src.id}
          source={src}
          isSelected={selectedSource === src.id}
          isRefreshing={refreshingSource === src.id}
          onSelect={onSelectSource}
          onToggle={onToggleSource}
          onEdit={onEditSource}
          onRefresh={onRefreshSource}
          onDelete={onDeleteSource}
        />
      ))}

      {sources.length === 0 && !loading && (
        <p className="text-slate-600 text-sm font-mono text-center py-4">
          Aucune source configurée
        </p>
      )}
    </div>
  );
};

export default SourcesList;
